// utils/pdfToImage.js
//
// Converte a 1ª página de um PDF (File ou Blob) em PNG (Blob).
// pdfjs-dist é pesado (~600KB); por isso fazemos import dinâmico —
// só é baixado quando o usuário realmente faz upload de um PDF.

let pdfjsLibPromise = null;

async function getPdfJs() {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = (async () => {
      const pdfjsLib = await import(
        /* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf"
      );
      const workerModule = await import(
        /* webpackChunkName: "pdfjs-worker" */ "pdfjs-dist/legacy/build/pdf.worker.entry"
      );
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        workerModule.default || workerModule;
      return pdfjsLib;
    })();
  }
  return pdfjsLibPromise;
}

/**
 * @param {File|Blob} file  PDF file/blob
 * @param {Object} opts
 * @param {number} opts.scale  Escala de renderização (default 2 → boa qualidade)
 * @returns {Promise<Blob>} PNG blob da primeira página
 */
export async function pdfFirstPageToPng(file, { scale = 2 } = {}) {
  const pdfjsLib = await getPdfJs();

  const arrayBuffer = await file.arrayBuffer();
  // SALVIA-746: sem `eval`. O pdf.js usa `new Function` para acelerar o
  // desenho de fontes, e a CSP (script-src sem 'unsafe-eval') o bloqueia;
  // desligado aqui, ele usa o caminho interpretado — mesmo resultado.
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer, isEvalSupported: false });
  const pdf = await loadingTask.promise;

  if (pdf.numPages < 1) {
    throw new Error("PDF sem páginas");
  }

  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  const context = canvas.getContext("2d");

  await page.render({ canvasContext: context, viewport }).promise;

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Falha ao gerar PNG do PDF"));
      },
      "image/png",
      0.92,
    );
  });
}
